import { getTrialStatus } from "@/services/user-service";
import { useUserStore } from "@/stores/user-store";
import { useEffect, useState } from "react";

interface TrialStatus {
	status: string;
	daysRemaining: number;
	endDate: string;
}

export const useTrialExpirationCheck = () => {
	const [showExpiredModal, setShowExpiredModal] = useState(false);
	const [trialStatus, setTrialStatus] = useState<TrialStatus | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const { user } = useUserStore();

	const checkTrialStatus = async () => {
		// Only check for trial users
		if (!user?.isTrialAccount && !user?.isTrial) {
			return;
		}

		// Don't check if account is already disabled
		if (user?.isAccountDisabled && user?.isTrial) {
			setShowExpiredModal(true);
			return;
		}

		try {
			setIsLoading(true);
			const status = await getTrialStatus();
			setTrialStatus(status);

			// Show modal if trial is expired
			if (status.status === "EXPIRED" || status.daysRemaining <= 0) {
				setShowExpiredModal(true);
			}
		} catch (error) {
			console.error("Failed to check trial status:", error);

			// If we can't check trial status but user is marked as trial and disabled,
			// assume it's expired
			if (user?.isAccountDisabled && user?.isTrial) {
				setShowExpiredModal(true);
			}
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		// Check trial status when component mounts and user changes
		if (user) {
			checkTrialStatus();
		}
	}, [user?.id, user?.isAccountDisabled, user?.isTrialAccount]);

	const closeExpiredModal = () => {
		setShowExpiredModal(false);
	};

	return {
		showExpiredModal,
		closeExpiredModal,
		trialStatus,
		isLoading,
		checkTrialStatus,
	};
};
